/**
 * Map Link Builders
 *
 * Pure builders for Google Maps and Apple Maps URLs. Nothing here opens a link, so the Mini
 * App and the studio website both use them (shared-client-screens D2); the Mini App's
 * Telegram opener stays in maps.ts.
 *
 * @module utils/mapLinks
 */

/**
 * Location parameters for building a maps URL.
 */
export interface LocationParams {
  /** Latitude coordinate */
  lat: number | null;
  /** Longitude coordinate */
  lng: number | null;
  /** Street address */
  address: string | null;
  /** City name */
  city: string | null;
  /** State/province */
  state: string | null;
}

/**
 * Build address string from location parts.
 */
function buildAddressString(location: LocationParams): string {
  return [location.address, location.city, location.state]
    .filter((part): part is string => Boolean(part))
    .join(', ');
}

/**
 * Build Google Maps URL from location data.
 */
export function buildGoogleMapsUrl(location: LocationParams): string | null {
  const baseUrl = 'https://www.google.com/maps/search/?api=1&query=';
  const addressString = buildAddressString(location);

  if (addressString) {
    return `${baseUrl}${encodeURIComponent(addressString)}`;
  }

  if (location.lat != null && location.lng != null) {
    return `${baseUrl}${location.lat},${location.lng}`;
  }

  return null;
}

/**
 * Build Apple Maps URL from location data.
 */
export function buildAppleMapsUrl(location: LocationParams): string | null {
  const addressString = buildAddressString(location);

  if (addressString) {
    return `https://maps.apple.com/?address=${encodeURIComponent(addressString)}`;
  }

  if (location.lat != null && location.lng != null) {
    return `https://maps.apple.com/?ll=${location.lat},${location.lng}`;
  }

  return null;
}

/**
 * @deprecated Use buildGoogleMapsUrl instead
 */
export function buildMapsUrl(location: LocationParams): string | null {
  return buildGoogleMapsUrl(location);
}
